// ============================================================================
//  Skeleton.tsx — NexusCorp ERP · Loading skeleton components
//  Usage: remplace les spinners centrés par des layouts qui préservent la forme
// ============================================================================

import { cn } from '@/lib/utils'

/* ── Base shimmer ────────────────────────────────────────────── */
export function Skeleton({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <div
      className={cn('nx-skeleton', className)}
      style={{ borderRadius: 6, ...style }}
    />
  )
}

/* ── KPI card skeleton ───────────────────────────────────────── */
export function KpiSkeleton() {
  return (
    <div style={{
      background: '#fff',
      border: '0.5px solid var(--bd)',
      borderRadius: 12,
      padding: '14px 16px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <Skeleton style={{ width: 28, height: 28, borderRadius: 7 }} />
        <Skeleton style={{ width: 48, height: 18, borderRadius: 99 }} />
      </div>
      <Skeleton style={{ width: '45%', height: 28, marginBottom: 6 }} />
      <Skeleton style={{ width: '65%', height: 13 }} />
    </div>
  )
}

/* ── Table row skeleton ──────────────────────────────────────── */
export function TableRowSkeleton({ cols = 5 }: { cols?: number }) {
  const widths = ['60%', '40%', '30%', '50%', '25%', '35%', '45%']
  return (
    <tr style={{ borderBottom: '0.5px solid var(--bd)' }}>
      {Array.from({ length: cols }).map((_, i) => (
        <td key={i} style={{ padding: '10px 14px' }}>
          <Skeleton style={{ width: widths[i % widths.length], height: 14 }} />
        </td>
      ))}
    </tr>
  )
}

/* ── Table skeleton (header + N rows) ───────────────────────── */
export function TableSkeleton({ rows = 6, cols = 5 }: { rows?: number; cols?: number }) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ borderBottom: '0.5px solid var(--bd)' }}>
          {Array.from({ length: cols }).map((_, i) => (
            <th key={i} style={{ padding: '8px 14px', textAlign: 'left' }}>
              <Skeleton style={{ width: `${40 + (i * 17) % 40}%`, height: 11 }} />
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: rows }).map((_, i) => (
          <TableRowSkeleton key={i} cols={cols} />
        ))}
      </tbody>
    </table>
  )
}

/* ── Card skeleton ───────────────────────────────────────────── */
export function CardSkeleton({ lines = 3 }: { lines?: number }) {
  return (
    <div style={{
      background: '#fff',
      border: '0.5px solid var(--bd)',
      borderRadius: 12,
      padding: '18px 20px',
    }}>
      <Skeleton style={{ width: '40%', height: 14, marginBottom: 12 }} />
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          style={{
            width: i === lines - 1 ? '55%' : '100%',
            height: 12,
            marginBottom: i < lines - 1 ? 8 : 0,
          }}
        />
      ))}
    </div>
  )
}

/* ── Page header skeleton ────────────────────────────────────── */
export function PageHeaderSkeleton() {
  return (
    <div style={{ marginBottom: 28 }}>
      <Skeleton style={{ width: 80, height: 12, marginBottom: 10 }} />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <Skeleton style={{ width: 200, height: 22, marginBottom: 8 }} />
          <Skeleton style={{ width: 280, height: 13 }} />
        </div>
        <Skeleton style={{ width: 120, height: 32, borderRadius: 8 }} />
      </div>
    </div>
  )
}

/* ── KPI grid skeleton (4 cards) ─────────────────────────────── */
export function KpiGridSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: `repeat(${Math.min(count, 4)}, 1fr)`,
      gap: 14,
      marginBottom: 24,
    }}>
      {Array.from({ length: count }).map((_, i) => (
        <KpiSkeleton key={i} />
      ))}
    </div>
  )
}

/* ── Dashboard full skeleton ─────────────────────────────────── */
export function DashboardSkeleton() {
  return (
    <div className="nx-fade-in">
      <PageHeaderSkeleton />
      <KpiGridSkeleton count={4} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <CardSkeleton lines={5} />
        <CardSkeleton lines={5} />
      </div>
    </div>
  )
}

/* ── List page skeleton (header + table) ─────────────────────── */
export function ListPageSkeleton({ cols = 5, rows = 8 }: { cols?: number; rows?: number }) {
  return (
    <div className="nx-fade-in">
      <PageHeaderSkeleton />
      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <Skeleton style={{ width: 220, height: 32, borderRadius: 8 }} />
        <div style={{ flex: 1 }} />
        <Skeleton style={{ width: 100, height: 32, borderRadius: 8 }} />
      </div>
      {/* Table */}
      <div style={{ background: '#fff', border: '0.5px solid var(--bd)', borderRadius: 12, overflow: 'hidden' }}>
        <TableSkeleton rows={rows} cols={cols} />
      </div>
    </div>
  )
}

/* ── Kanban board skeleton ───────────────────────────────────── */
export function KanbanSkeleton({ cols = 4 }: { cols?: number }) {
  return (
    <div className="nx-fade-in" style={{ display: 'flex', gap: 14, overflowX: 'auto', paddingBottom: 8 }}>
      {Array.from({ length: cols }).map((_, ci) => (
        <div key={ci} style={{
          minWidth: 260,
          background: 'var(--surface-2)',
          borderRadius: 10,
          padding: 12,
          border: '0.5px solid var(--bd)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <Skeleton style={{ width: 10, height: 10, borderRadius: '50%' }} />
            <Skeleton style={{ width: 80, height: 13 }} />
            <Skeleton style={{ width: 22, height: 18, borderRadius: 99, marginLeft: 'auto' }} />
          </div>
          {Array.from({ length: 3 + (ci % 2) }).map((_, ri) => (
            <div key={ri} style={{
              background: '#fff',
              border: '0.5px solid var(--bd)',
              borderRadius: 8,
              padding: '10px 12px',
              marginBottom: 8,
            }}>
              <Skeleton style={{ width: '80%', height: 13, marginBottom: 8 }} />
              <div style={{ display: 'flex', gap: 6 }}>
                <Skeleton style={{ width: 50, height: 18, borderRadius: 99 }} />
                <Skeleton style={{ width: 60, height: 18, borderRadius: 99 }} />
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}
